import { useState, useEffect } from "react";
import { API_ENDPOINTS } from "@/constants/constants";
import {
  RefreshCw,
  Search,
  PlusCircle,
  X,
  Edit,
  Trash2,
} from "lucide-react";

// Interface pour les ministères
interface Ministry {
  id: number;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

// Interface pour le formulaire
interface MinistryForm {
  name: string;
  description: string;
}

const AdminMinistries = () => {
  const [ministries, setMinistries] = useState<Ministry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedMinistry, setSelectedMinistry] = useState<Ministry | null>(
    null
  );
  const [createForm, setCreateForm] = useState<MinistryForm>({
    name: "",
    description: "",
  });
  const [updateForm, setUpdateForm] = useState<MinistryForm>({
    name: "",
    description: "",
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createSuccess, setCreateSuccess] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState("");
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<number | null>(null);

  useEffect(() => {
    fetchMinistries();
  }, []);

  const fetchMinistries = async () => {
    setLoading(true);
    setError("");

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      const response = await fetch(
        API_ENDPOINTS.MINISTRIES?.GET_MINISTRIES ||
          "http://localhost:3000/api/v1/ministries",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || "Erreur lors de la récupération des ministères"
        );
      }

      setMinistries(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue");
      console.error("Erreur de récupération des ministères:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMinistry = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateError("");
    setCreateSuccess(false);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      const response = await fetch(
        API_ENDPOINTS.MINISTRIES?.CREATE_MINISTRY,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(createForm),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || "Erreur lors de la création du ministère"
        );
      }

      fetchMinistries();

      setCreateForm({
        name: "",
        description: "",
      });
      setCreateSuccess(true);

      setTimeout(() => {
        setIsCreateModalOpen(false);
        setCreateSuccess(false);
      }, 2000);
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur de création du ministère:", err);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditMinistry = (ministry: Ministry) => {
    setSelectedMinistry(ministry);
    setUpdateForm({
      name: ministry.name,
      description: ministry.description,
    });
    setIsUpdateModalOpen(true);
  };

  const handleUpdateMinistry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMinistry) return;

    setUpdateLoading(true);
    setUpdateError("");
    setUpdateSuccess(false);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      const updateUrl = API_ENDPOINTS.MINISTRIES?.UPDATE_MINISTRY(selectedMinistry.id);

      const response = await fetch(updateUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(updateForm),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || "Erreur lors de la mise à jour du ministère"
        );
      }

      fetchMinistries();

      setUpdateSuccess(true);

      setTimeout(() => {
        setIsUpdateModalOpen(false);
        setUpdateSuccess(false);
        setSelectedMinistry(null);
      }, 2000);
    } catch (err) {
      setUpdateError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur de mise à jour du ministère:", err);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteMinistry = async (id: number) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce ministère?")) return;

    setDeleteLoading(id);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      const deleteUrl = API_ENDPOINTS.MINISTRIES?.DELETE_MINISTRY(id);

      const response = await fetch(deleteUrl, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(
          data.message || "Erreur lors de la suppression du ministère"
        );
      }

      fetchMinistries();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue");
      console.error("Erreur de suppression du ministère:", err);
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setCreateForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setUpdateForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const filteredMinistries = ministries.filter(
    (ministry) =>
      ministry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ministry.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold">Gestion des Ministères</h1>
      <p className="mt-4">Gérez les ministères de la plateforme.</p>

      <div className="flex justify-between items-center mt-8">
        <div className="relative w-64">
          <input
            type="text"
            placeholder="Rechercher un ministère..."
            className="w-full p-2 pl-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={16}
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
          >
            <PlusCircle size={16} />
            Créer un ministère
          </button>

          <button
            onClick={fetchMinistries}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Actualiser
          </button>
        </div>
      </div>

      <div className="mt-8 bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Liste des ministères</h2>

        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
          </div>
        )}

        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && filteredMinistries.length === 0 && (
          <p>Aucun ministère trouvé.</p>
        )}

        {!loading && !error && filteredMinistries.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                    N°
                  </th>
                  <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                    Nom
                  </th>
                  <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                    Description
                  </th>
                  <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                    Date de création
                  </th>
                  <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredMinistries.map((ministry, index) => (
                  <tr key={ministry.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4 border-b text-center">
                      {index + 1}
                    </td>
                    <td className="py-3 px-4 border-b font-medium">
                      {ministry.name}
                    </td>
                    <td className="py-3 px-4 border-b">
                      {ministry.description?.substring(0, 50)}
                      {ministry.description?.length > 50 ? "..." : ""}
                    </td>
                    <td className="py-3 px-4 border-b">
                      {new Date(ministry.createdAt).toLocaleString("fr-FR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="py-3 px-4 border-b">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditMinistry(ministry)}
                          className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
                          title="Modifier"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteMinistry(ministry.id)}
                          className="p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded"
                          title="Supprimer"
                          disabled={deleteLoading === ministry.id}
                        >
                          {deleteLoading === ministry.id ? (
                            <div className="animate-spin h-4 w-4 border-2 border-red-500 rounded-full border-t-transparent"></div>
                          ) : (
                            <Trash2 size={18} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de création de ministère */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setIsCreateModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-6">Créer un ministère</h2>

            {createSuccess && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
                <p>Ministère créé avec succès!</p>
              </div>
            )}

            {createError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                <p>{createError}</p>
              </div>
            )}

            <form onSubmit={handleCreateMinistry}>
              <div className="mb-4">
                <label htmlFor="create-ministry-name" className="block text-gray-700 text-sm font-bold mb-2">
                  Nom du ministère *
                </label>
                <input
                  id="create-ministry-name"
                  type="text"
                  name="name"
                  value={createForm.name}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="create-ministry-description" className="block text-gray-700 text-sm font-bold mb-2">
                  Description
                </label>
                <textarea
                  id="create-ministry-description"
                  name="description"
                  value={createForm.description}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-32"
                  placeholder="Description du ministère"
                  required
                />
              </div>

              <div className="flex justify-end mt-6 sticky bottom-0 bg-white py-3 border-t">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors flex items-center gap-2"
                  disabled={createLoading}
                >
                  {createLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  Créer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de modification de ministère */}
      {isUpdateModalOpen && selectedMinistry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setIsUpdateModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-6">Modifier le ministère</h2>

            {updateSuccess && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
                <p>Ministère modifié avec succès!</p>
              </div>
            )}

            {updateError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                <p>{updateError}</p>
              </div>
            )}

            <form onSubmit={handleUpdateMinistry}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="mb-4">
                    <label htmlFor="update-ministry-name" className="block text-gray-700 text-sm font-bold mb-2">
                      Nom du ministère *
                    </label>
                    <input
                      id="update-ministry-name"
                      type="text"
                      name="name"
                      value={updateForm.name}
                      onChange={handleUpdateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="update-ministry-description" className="block text-gray-700 text-sm font-bold mb-2">
                  Description
                </label>
                <textarea
                  id="update-ministry-description"
                  name="description"
                  value={updateForm.description}
                  onChange={handleUpdateInputChange}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-32"
                  placeholder="Description du ministère"
                  required
                />
              </div>

              <div className="flex justify-end mt-6 sticky bottom-0 bg-white py-3 border-t">
                <button
                  type="button"
                  onClick={() => setIsUpdateModalOpen(false)}
                  className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors flex items-center gap-2"
                  disabled={updateLoading}
                >
                  {updateLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  Mettre à jour
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMinistries;
